import { buildMissedLinks, type MissedLink } from '@/domain/missedLinks';
import type { Booking, CustomerRecord, Deposit, StudioId } from '@/domain/types';

// A gap-fill link holds the slot for 24h (board 08). Past that it's a "missed
// deposit" the sweeper chases each morning; past 48h still-unclosed it escalates
// to an Ops report.
export const MISSED_DEPOSIT_MIN_HOURS = 24;
export const MISSED_DEPOSIT_OPS_HOURS = 48;

export interface MissedDepositRow {
  depositId: string;
  customer?: CustomerRecord;
  customerName: string;
  studioId: StudioId;
  amountAud: number;
  /** Whole hours since the link was sent. */
  hoursSinceSent: number;
  /** Session the held link is against, when known. */
  sessionIso?: string;
  /** Unpaid past 48h — escalated to an Ops report. */
  escalated: boolean;
  /** W5: the routed link behind the row (sender, slot state, pay-by). */
  link: MissedLink;
}

/**
 * The sweeper's "missed deposits" morning list (bd tv-d2f, board 08): the
 * SWEEPER-sent unpaid links (W5 DECIDED 24 Sep — agent links are the Agent's
 * call-back, messenger links an Ops item) past the 24h lock and not closed. A
 * lapsed link counts too: the firm gap-fill hold EXPIRES at 24h, which used to
 * drop it off this list the moment the reconciler ran. Links unpaid past 48h are
 * flagged as escalated (Ops sees them read-only). Ordered most-overdue first.
 * Network-wide unless scoped by `studioIds`. Built on the pure `missedLinks`.
 */
export function buildMissedDeposits(args: {
  nowIso: string;
  deposits: Record<string, Deposit>;
  customers: Record<string, CustomerRecord>;
  bookings: Record<string, Booking>;
  studioIds?: StudioId[];
}): MissedDepositRow[] {
  const { nowIso, deposits, customers, bookings, studioIds } = args;
  return buildMissedLinks({ nowIso, deposits, bookings, studioIds })
    .filter((l) => l.owner === 'sweeper')
    .map((l): MissedDepositRow => {
      const d = deposits[l.depositId]!;
      const customer = customers[l.customerId];
      return {
        depositId: l.depositId,
        customer,
        customerName: customer ? `${customer.firstName} ${customer.lastName}` : (d.depositReference ?? d.id),
        studioId: l.studioId,
        amountAud: l.amountAud,
        hoursSinceSent: l.ageH,
        sessionIso: l.sessionIso,
        escalated: l.ageH >= MISSED_DEPOSIT_OPS_HOURS,
        link: l,
      };
    });
}
